// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableRow, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { xHasRoles } from "@pace/App/Store/Selectors/SessionSelectors";
import Button, { ButtonPresentationProps } from "@pace/MobiusOverrides/Button/Button";
import { EMPTY_VALUE, sharedDateTimeOptions } from "@pace/XDL/constants";
import { parseBoolean } from "@pace/XDL/Utilities/helpers";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    rfqJobQuotesPageUrl: getPageLinkByPageType(state, "RfqJobQuotesPage")?.url,
    cartState: getCurrentCartState(state),
    // XNG-Changes
    cannotAddToCart: xHasRoles(state, ["CannotAddToCart"]),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataCells?: DataTableCellProps;
    clickableCell?: LinkPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    cartNotificationModal?: TwoButtonModalStyles;
    actionsCells?: DataTableCellProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: {
        gap: 5,
    },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({
    quote,
    // XNG-Changes
    history,
    checkoutShippingPageUrl,
    session,
    cartState,
    quoteSettings,
    extendedStyles,
    cannotAddToCart,
}) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));
    const [quoteNotificationModalIsOpen, setQuoteNotificationModalIsOpen] = React.useState(false);

    const showAddToCartButton =
        !quote.failedToGetRealTimeInventory &&
        !quote.isSalesperson &&
        parseBoolean(quote.properties.canAcceptQuote) &&
        // XNG-Changes: PAC-778
        !cannotAddToCart;

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            ...sharedDateTimeOptions,
        },
    });

    // let expirationDateDisplay = "";
    // if (quote.expirationDate) {
    //     expirationDateDisplay = getLocalizedDateTime({
    //         dateTime: quote.expirationDate,
    //         language: session.language,
    //         options: {
    //             year: "numeric",
    //             month: "numeric",
    //             day: "numeric",
    //         },
    //     });
    // }
    // const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    // XNG-Changes
    const redirectToCheckout = () => {
        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${quote.id}`);
        }
    };

    const submitQuoteCheckoutHandler = () => {
        redirectToCheckout();
    };

    const cancelQuoteCheckoutHandler = () => {
        setQuoteNotificationModalIsOpen(false);
    };

    // XNG-Changes
    const addToCartClickHandler = () => {
        if (cartState.value?.lineCount === 0) {
            redirectToCheckout();
        } else {
            setQuoteNotificationModalIsOpen(true);
        }
    };

    // XNG-Changes
    return (
        <>
            <DataTableRow data-test-selector="recentQuotes">
                <DataTableCell {...styles.clickableCell}>
                    {/* Xng-Change: display Erp quote.erpOrderNumber if exist instead of Opti quoteNumber  */}
                    <RfqQuoteDetailsPageTypeLink title={quote.erpOrderNumber || quote.quoteNumber} quoteId={quote.id} />
                </DataTableCell>
                <DataTableCell {...styles.dataCells}>
                    <Typography>{quote.statusDisplay}</Typography>
                </DataTableCell>
                <DataTableCell {...styles.dataCells}>
                    <Typography>{quote.properties.jobNumber}</Typography>
                </DataTableCell>
                <DataTableCell tight {...styles.dataCells}>
                    <Typography>{orderDateDisplay}</Typography>
                </DataTableCell>
                <DataTableCell {...styles.dataCells}>
                    <Typography>{quote.totalQtyOrdered || EMPTY_VALUE}</Typography>
                </DataTableCell>
                <DataTableCell {...styles.dataCells}>
                    <Typography>{quote.orderSubTotalDisplay}</Typography>
                </DataTableCell>
                <DataTableCell {...styles.actionsCells}>
                    {showAddToCartButton && (
                        <Button
                            {...styles.addToCartButton}
                            onClick={() => addToCartClickHandler()}
                            data-test-selector="recentQuotes_addToCartButton"
                            aria-label="Add To Cart"
                        >
                            {translate("Add To Cart")}
                        </Button>
                    )}
                </DataTableCell>
            </DataTableRow>
            <TwoButtonModal
                {...styles.cartNotificationModal}
                modalIsOpen={quoteNotificationModalIsOpen}
                headlineText={translate("Cart Notification")}
                messageText={siteMessage("Custom_Rfq_CartNotificationMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={cancelQuoteCheckoutHandler}
                onSubmit={submitQuoteCheckoutHandler}
                submitTestSelector="QuoteAddToCartCartNotification"
            />
        </>
    );
};

export default connect(mapStateToProps)(withHistory(QuoteSummaryCard));
